import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Custom } from './customTypes';

// ✅ Enhanced State Interface
interface CustomFilters {
  search: string;
  status: 'all' | 'draft' | 'published' | 'under_verification' | 'rejected';
  category_id?: string;
  venue_id?: string;
  sortBy: 'createdAt' | 'start_date' | 'title' | 'view_count';
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
}

interface customState {
  // ✅ Data
  currentCustom: Custom | null;
  custom: Custom[];
  categories: any[];
  venues: any[];
  
  // ✅ UI State
  filters: CustomFilters;
  selectedCustomId: string | null;
  
  // ✅ Loading States
  loading: boolean;
  error: string | null;
  
  // ✅ Pagination
  total: number;
  hasMore: boolean;
  
  // ✅ View Mode (for customPage)
  viewMode: 'grid' | 'list';
  
  // ✅ Modal States
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  CustomToDelete: string | null;
}

// ✅ Initial State
const initialState: customState = {
  // Data
  currentCustom: null,
  custom: [],
  categories: [],
  venues: [],
  
  // Filters
  filters: {
    search: '',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: 12,
  },
  
  // UI State
  selectedCustomId: null,
  viewMode: 'grid',
  
  // Loading
  loading: false,
  error: null,
  
  // Pagination
  total: 0,
  hasMore: true,
  
  // Modals
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  CustomToDelete: null,
};

// ✅ Slice
const customSlice = createSlice({
  name: 'custom',
  initialState,
  reducers: {
    // ✅ Custom Selection
    setCurrentCustom: (state, action: PayloadAction<Custom | null>) => {
      state.currentCustom = action.payload;
      state.selectedCustomId = action.payload?.documentId || null;
    },
    
    setSelectedCustomId: (state, action: PayloadAction<string | null>) => {
      state.selectedCustomId = action.payload;
    },
    
    // ✅ Filter Management
    setFilters: (state, action: PayloadAction<Partial<CustomFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      
      // Reset to page 1 when filters change (except pagination)
      if (action.payload.page === undefined) {
        state.filters.page = 1;
      }
    },
    
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.filters.page = 1; // Reset pagination
    },
    
    setStatus: (state, action: PayloadAction<CustomFilters['status']>) => {
      state.filters.status = action.payload;
      state.filters.page = 1;
    },
    
    setCategory: (state, action: PayloadAction<string | undefined>) => {
      state.filters.category_id = action.payload;
      state.filters.page = 1;
    },
    
    setVenue: (state, action: PayloadAction<string | undefined>) => {
      state.filters.venue_id = action.payload;
      state.filters.page = 1;
    },
    
    setSortBy: (state, action: PayloadAction<CustomFilters['sortBy']>) => {
      state.filters.sortBy = action.payload;
    },
    
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.filters.sortOrder = action.payload;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    // ✅ View Mode
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    
    // ✅ Modal Management
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    
    openEditModal: (state, action: PayloadAction<string>) => {
      state.isEditModalOpen = true;
      state.selectedCustomId = action.payload;
    },
    
    closeEditModal: (state) => {
      state.isEditModalOpen = false;
      state.selectedCustomId = null;
    },
    
    openDeleteModal: (state, action: PayloadAction<string>) => {
      state.isDeleteModalOpen = true;
      state.CustomToDelete = action.payload;
    },
    
    closeDeleteModal: (state) => {
      state.isDeleteModalOpen = false;
      state.CustomToDelete = null;
    },
    
    // ✅ Error Handling
    clearcustomError: (state) => {
      state.error = null;
    },
    
    // ✅ Manual Data Updates (for optimistic updates)
    addCustom: (state, action: PayloadAction<Custom>) => {
      state.custom.unshift(action.payload);
      state.total += 1;
    },
    
    updateCustom: (state, action: PayloadAction<Custom>) => {
      const index = state.custom.findIndex(e => e.documentId === action.payload.documentId);
      if (index !== -1) {
        state.custom[index] = action.payload;
      }
      if (state.currentCustom?.documentId === action.payload.documentId) {
        state.currentCustom = action.payload;
      }
    },
    
    removeCustom: (state, action: PayloadAction<string>) => {
      state.custom = state.custom.filter(e => e.documentId !== action.payload);
      state.total -= 1;
      if (state.currentCustom?.documentId === action.payload) {
        state.currentCustom = null;
      }
    },
  },
});

// ✅ Actions Export
export const {
  // Selection
  setCurrentCustom,
  setSelectedCustomId,
  
  // Filters
  setFilters,
  setSearch,
  setStatus,
  setCategory,
  setVenue,
  setSortBy,
  setSortOrder,
  setPage,
  resetFilters,
  
  // View
  setViewMode,
  
  // Modals
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  openDeleteModal,
  closeDeleteModal,
  
  // Error
  clearcustomError,
  
  // Manual Updates
  addCustom,
  updateCustom,
  removeCustom,
} = customSlice.actions;

// ✅ Reducer Export
export default customSlice.reducer;

// ✅ SELECTORS (Bonus for easy access)
export const selectCustomFilters = (state: { custom: customState }) => state.custom.filters;
export const selectCurrentCustom = (state: { custom: customState }) => state.custom.currentCustom;
export const selectSelectedCustomId = (state: { custom: customState }) => state.custom.selectedCustomId;
export const selectViewMode = (state: { custom: customState }) => state.custom.viewMode;
export const selectcustomLoading = (state: { custom: customState }) => state.custom.loading;
export const selectcustomError = (state: { custom: customState }) => state.custom.error;
